import { cn } from '../../../shared/lib/cn';
import { clockTime } from '../../../shared/lib/format';
import type { TSource } from '../api';
import type { TSourceActivity, TSourceVerification } from '../lib';
import {
  activityLine,
  failureModeLine,
  verificationLine,
  verificationState,
  verificationWhere,
} from '../lib';

const dotOf = (verification: TSourceVerification | undefined): string => {
  if (verificationState(verification) !== 'counted') return 'bg-faint';
  const checked = verification?.checked ?? null;
  const passed = verification?.passed ?? null;
  if (checked === null || passed === null || checked === 0) return 'bg-faint';
  return passed === checked ? 'bg-success' : 'bg-danger';
};

export const VerificationLine = ({
  verification,
  activity,
  unchecked,
  onAuthFailure,
  timeZone,
  className,
}: {
  verification: TSourceVerification | undefined;
  activity?: TSourceActivity;
  unchecked?: boolean;
  onAuthFailure?: TSource['onAuthFailure'];
  timeZone?: string;
  className?: string;
}) => {
  const counted = verificationState(verification) === 'counted';
  const where = unchecked ? null : verificationWhere(verification);
  const asOf = verification?.computedAt
    ? `as of ${clockTime(verification.computedAt, timeZone)}`
    : null;

  return (
    <div className={cn('border-t border-border-sunken pt-5', className)}>
      <div className="flex items-center gap-inset">
        <span aria-hidden className={cn('size-dot shrink-0 rounded-full', dotOf(verification))} />
        <span className={cn('text-data', counted || unchecked ? 'text-primary' : 'text-muted')}>
          {unchecked
            ? activityLine('unchecked', activity, { timeZone })
            : verificationLine(verification)}
        </span>
        {!unchecked && asOf && (
          <span className="ml-auto shrink-0 font-mono text-micro text-faint">{asOf}</span>
        )}
      </div>
      {where && <p className="mt-1 pl-5 font-mono text-meta text-muted">{where}</p>}
      {!unchecked && onAuthFailure && (
        <p className="mt-1 pl-5 text-meta text-muted">{failureModeLine(onAuthFailure)}</p>
      )}
    </div>
  );
};
